import React from 'react';
import { Card as MuiCard, CardProps as MuiCardProps } from '@mui/material';
import { PounceStyleProps, extractPounceProps, mapPouncePropsToSx } from '../../utils';

export type CardVariant =
  | 'mid'
  | 'midToBrand'
  | 'midToWhite'
  | 'inverseRaised'
  | 'inverseRaisedToBrand'
  | 'transparent';

export interface CardProps
  extends Omit<MuiCardProps, 'variant' | keyof PounceStyleProps>, PounceStyleProps {
  /** Whether the card should be light blue-navy or dark blue-navy */
  variant?: CardVariant;

  /** The HTML element or React component to render as the Card */
  as?: React.ElementType;

  /** Additional styles to apply (combined with pounce props) */
  sx?: MuiCardProps['sx'];
}

/**
 * Card component built on top of MUI Card.
 *
 * Provides all the variants and functionality from the original Pounce Card
 * while leveraging MUI's accessibility and interaction patterns.
 */
export const Card = React.forwardRef<HTMLDivElement, CardProps>(function Card(
  { variant = 'mid', as, children, sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  // Combine pounce-generated styles with user sx
  const combinedSx: MuiCardProps['sx'] = {
    ...pounceStyles,
    ...(sx || {}),
  } as MuiCardProps['sx'];

  return (
    <MuiCard ref={ref} component={as} variant={variant} sx={combinedSx} {...otherProps}>
      {children}
    </MuiCard>
  );
}) as React.FC<CardProps>;
